import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { SectionHeader } from "@/components/SectionHeader";
import { SettingsRow, S_INPUT } from "@/components/SettingsRow";
import { CurrentUser } from "@/api/auth";
import { ApiError } from "@/api/client";
import { changeMyPassword, updateMyDisplayName } from "@/api/settings";

type Props = { user: CurrentUser | null };

export function ProfilSection({ user }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState(user?.full_name || "");
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<string | null>(null);

  useEffect(() => {
    setName(user?.full_name || "");
  }, [user?.full_name]);

  const [pwOpen, setPwOpen] = useState(false);
  const [pwAlt, setPwAlt] = useState("");
  const [pwNeu, setPwNeu] = useState("");
  const [pwWdh, setPwWdh] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const saveName = async () => {
    if (!name.trim()) return;
    setSavingName(true);
    setNameMsg(null);
    try {
      await updateMyDisplayName(name.trim());
      await qc.invalidateQueries({ queryKey: ["me"] });
      setNameMsg("Gespeichert.");
    } catch (e) {
      setNameMsg(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingName(false);
    }
  };

  const submitPw = async () => {
    setPwMsg(null);
    if (pwNeu.length < 8) {
      setPwMsg({ kind: "err", text: "Neues Passwort muss mindestens 8 Zeichen haben." });
      return;
    }
    if (pwNeu !== pwWdh) {
      setPwMsg({ kind: "err", text: "Wiederholung stimmt nicht überein." });
      return;
    }
    setPwBusy(true);
    try {
      await changeMyPassword(pwAlt, pwNeu);
      setPwMsg({ kind: "ok", text: "Passwort geändert." });
      setPwAlt("");
      setPwNeu("");
      setPwWdh("");
      setPwOpen(false);
    } catch (e) {
      setPwMsg({ kind: "err", text: e instanceof ApiError ? e.message : "Fehler." });
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <Card>
      <SectionHeader
        title="Profil"
        desc="Ihre Angaben werden in E-Mails und Bestätigungen als Absender verwendet."
      />
      <SettingsRow label="Benutzername" hint="Login-Name, kann nicht geändert werden">
        <div style={{ ...S_INPUT, background: "var(--bg-subtle)", color: "var(--fg-muted)" }}>
          {user?.username || "—"}
        </div>
      </SettingsRow>
      <SettingsRow label="Anzeigename" hint="Wird als Absendername in E-Mails verwendet">
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            style={S_INPUT}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ihr Name"
          />
          <Btn variant="primary" icon="check" size="sm" onClick={saveName} disabled={savingName}>
            Speichern
          </Btn>
          {nameMsg && (
            <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>{nameMsg}</span>
          )}
        </div>
      </SettingsRow>
      <SettingsRow label="Rolle" hint="Vom Administrator vergeben">
        <div style={{ ...S_INPUT, background: "var(--bg-subtle)", color: "var(--fg-muted)" }}>
          {(user?.role || "viewer").charAt(0).toUpperCase() + (user?.role || "viewer").slice(1)}
        </div>
      </SettingsRow>

      <SettingsRow label="Passwort" hint="Mindestens 8 Zeichen" align="flex-start">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {!pwOpen && (
            <Btn variant="secondary" icon="lock_reset" size="sm" onClick={() => setPwOpen(true)}>
              Passwort ändern
            </Btn>
          )}
          {pwOpen && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 360 }}>
              <input
                style={S_INPUT}
                type="password"
                placeholder="Aktuelles Passwort"
                value={pwAlt}
                onChange={(e) => setPwAlt(e.target.value)}
                autoComplete="current-password"
              />
              <input
                style={S_INPUT}
                type="password"
                placeholder="Neues Passwort"
                value={pwNeu}
                onChange={(e) => setPwNeu(e.target.value)}
                autoComplete="new-password"
              />
              <input
                style={S_INPUT}
                type="password"
                placeholder="Neues Passwort wiederholen"
                value={pwWdh}
                onChange={(e) => setPwWdh(e.target.value)}
                autoComplete="new-password"
              />
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="primary" icon="check" size="sm" onClick={submitPw} disabled={pwBusy}>
                  Ändern
                </Btn>
                <Btn
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPwOpen(false);
                    setPwAlt("");
                    setPwNeu("");
                    setPwWdh("");
                    setPwMsg(null);
                  }}
                >
                  Abbrechen
                </Btn>
              </div>
            </div>
          )}
          {pwMsg && (
            <div
              style={{
                fontSize: 12,
                color: pwMsg.kind === "ok" ? "var(--status-confirm)" : "#b44a4a",
              }}
            >
              {pwMsg.text}
            </div>
          )}
        </div>
      </SettingsRow>
    </Card>
  );
}
